import { convertToImperialDate } from "app/codes";
import React, { useEffect, useMemo, useState } from "react";
import {
  getAlarms,
  getAssetAlarms,
  ackAlarm,
  getStatusStyle,
  compareAlarms,
  isHseAlarm,
} from "service/events";

// Direction glyph for set-point breach (AV-D1 field: direction, high/low).
function directionGlyph(direction) {
  if (!direction) return "";
  const d = String(direction).toLowerCase();
  if (d.startsWith("hi") || d === "high" || d === ">" || d === "above")
    return "▲";
  if (d.startsWith("lo") || d === "low" || d === "<" || d === "below")
    return "▼";
  return direction;
}

function AlarmTable(props) {
  const { wellId, assetId } = props;
  const { loadingIconActions } = props;
  const { search = "", severityFilter = "ALL", hideAcked = false } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];
  const [data, setData] = useState(null);
  // AV-D3: ack overrides keyed by alarm id. On a successful POST .../ack the BE
  // returns the persisted row, so we store { ack_by, ack_time } and render those
  // real values. If the call fails we still fall back to an optimistic flag so
  // the operator sees their action take effect ({ ack_by: null } => "Ack'd").
  const [ackedLocal, setAckedLocal] = useState({}); // { [id]: { ack_by, ack_time } }
  const [mutedLocal, setMutedLocal] = useState({}); // { [id]: true }

  useEffect(() => {
    if (wellId == null && assetId == null) {
      return;
    }
    setAckedLocal({});
    setMutedLocal({});
    showLoadingIcon && showLoadingIcon();
    const onOk = (result) => {
      setData(Array.isArray(result) ? result : []);
      hideLoadingIcon && hideLoadingIcon();
    };
    const onErr = () => {
      hideLoadingIcon && hideLoadingIcon();
    };
    if (wellId != null) {
      getAlarms(wellId, onOk, onErr);
    } else if (assetId != null) {
      getAssetAlarms(assetId, onOk, onErr);
    }
  }, [wellId, assetId, hideLoadingIcon, showLoadingIcon]);

  // AV-D2 + AV-D3: filter (search/severity/acked) then sort (HSE → severity → time).
  const rows = useMemo(() => {
    if (!data) return null;
    const term = search.trim().toLowerCase();
    return data
      .filter((item) => {
        // Acked if the BE row already carries ack_by, or we have a local
        // override (from a successful/optimistic ack this session).
        const isAcked = ackedLocal[item.id] != null || item.ack_by;
        if (hideAcked && isAcked) return false;
        if (
          severityFilter !== "ALL" &&
          String(item.severity || "").toLowerCase() !==
            severityFilter.toLowerCase()
        )
          return false;
        if (term) {
          const hay = [
            item.tag,
            item.event_type,
            item.parameter,
            item.well_name,
            item.asset_name,
            item.severity,
            item.status,
            item.reporter,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!hay.includes(term)) return false;
        }
        return true;
      })
      .slice()
      .sort(compareAlarms);
  }, [data, search, severityFilter, hideAcked, ackedLocal]);

  // AV-D3: call POST .../alarms/<id>/ack and write the returned ack_by/ack_time
  // back onto the row (no longer a pure local flag). If the endpoint fails we
  // keep the optimistic override so the UI still reflects the operator action.
  const ackOne = (id) => {
    ackAlarm(
      id,
      (row) => {
        setAckedLocal((p) => ({
          ...p,
          [id]: {
            ack_by: (row && row.ack_by) || "me",
            ack_time: (row && row.ack_time) || null,
          },
        }));
      },
      () => {
        setAckedLocal((p) => ({ ...p, [id]: { ack_by: null, ack_time: null } }));
      }
    );
  };
  const muteOne = (id) =>
    setMutedLocal((p) => ({ ...p, [id]: !p[id] }));

  return (
    <>
      <table className="table table-borderless table-centered table-nowrap">
        <thead className="">
          <tr>
            <th>#</th>
            <th>Asset</th>
            <th>Well Name</th>
            <th>Tag / Parameter</th>
            <th>Event Type</th>
            <th>Set-point</th>
            <th>Current</th>
            <th>Date</th>
            <th>Status</th>
            <th>Severity</th>
            <th>Reporter</th>
            <th>Ack</th>
          </tr>
        </thead>
        {rows && (
          <tbody>
            {rows.map((item, index) => {
              const severity = getStatusStyle(item.severity);
              const hse = isHseAlarm(item);
              // Effective ack: prefer the session override (carries the BE
              // ack_by/ack_time returned by POST .../ack), else the row's own
              // persisted BE fields.
              const ackOverride = ackedLocal[item.id];
              const ackBy = ackOverride ? ackOverride.ack_by : item.ack_by;
              const ackTime = ackOverride ? ackOverride.ack_time : item.ack_time;
              const isAcked = ackOverride != null || item.ack_by;
              const isMuted = mutedLocal[item.id];
              return (
                <tr
                  key={item.id ?? index}
                  style={
                    hse
                      ? {
                          background: "rgba(255, 84, 84, 0.08)",
                          borderLeft: "4px solid #FF5454",
                        }
                      : undefined
                  }
                >
                  <td>
                    {hse && (
                      <i
                        className="bi bi-exclamation-triangle-fill me-1"
                        style={{ color: "#FF5454" }}
                        title="HSE: H2S / combustible gas — highest priority"
                      ></i>
                    )}
                    #{item.id}
                  </td>
                  <td>{item.asset_name}</td>
                  <td className="fw-semibold text-start">{item.well_name}</td>
                  <td className="fw-semibold text-start">
                    {hse && (
                      <span
                        className="badge me-1"
                        style={{ background: "#FF5454", color: "#fff" }}
                      >
                        HSE
                      </span>
                    )}
                    {item.tag || item.parameter || "—"}
                  </td>
                  <td className="text-muted fw-semibold text-start">
                    {item.event_type}
                  </td>
                  <td className="text-start">
                    {item.set_point != null ? item.set_point : "—"}{" "}
                    <span style={{ color: "#FF8C00" }}>
                      {directionGlyph(item.direction)}
                    </span>
                  </td>
                  <td className="fw-semibold text-start">
                    {item.current_value != null ? item.current_value : "—"}
                  </td>
                  <td className="text-muted fw-semibold text-start">
                    {convertToImperialDate(item.start_date)}
                  </td>
                  <td className="text-muted fw-semibold text-start">
                    {item.status}
                  </td>
                  <td className="text-start p-1">
                    <div
                      className="d-flex flex-row justify-content-center align-items-center p-1"
                      style={{
                        color: severity.textColor,
                        background: severity.bg,
                        borderRadius: "4px",
                      }}
                    >
                      <span>{item.severity || "—"}</span>
                    </div>
                  </td>
                  <td>{item.reporter}</td>
                  <td>
                    {isAcked ? (
                      <span
                        className="text-muted"
                        title={
                          ackBy
                            ? `Acked by ${ackBy} ${
                                ackTime ? convertToImperialDate(ackTime) : ""
                              }`
                            : "Acknowledged (pending sync)"
                        }
                      >
                        <i className="bi bi-check2-circle me-1"></i>
                        {ackBy || "Ack'd"}
                      </span>
                    ) : (
                      <div className="d-flex flex-row">
                        <button
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => ackOne(item.id)}
                          title="Acknowledge alarm"
                        >
                          Ack
                        </button>
                        <button
                          className={`btn btn-sm ${
                            isMuted
                              ? "btn-secondary"
                              : "btn-outline-secondary"
                          }`}
                          onClick={() => muteOne(item.id)}
                          title="Mute / unmute"
                        >
                          <i
                            className={`bi ${
                              isMuted ? "bi-bell-slash" : "bi-bell"
                            }`}
                          ></i>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </>
  );
}

export default AlarmTable;
